import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Share2, Download, Loader2, ImageIcon, BarChart3, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { ATTR_META } from './SkinReportCard';

const W = 1080, H = 1350;
const CREAM = '#FFF2EC', INK = '#1A1514', GREEN = '#FF6B4A', SAGE = '#4ADE80', MUTED = '#8C7A76';

const toSrc = (img) => (img?.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`);
const loadImage = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im);
  im.onerror = reject;
  im.src = src;
});
const overallOf = (a) => a?.overall_score ?? (a?.health_score != null ? a.health_score * 10 : null);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const topImprovements = (before, after, n = 4) => {
  const bs = before?.scores || {}, as = after?.scores || {};
  return Object.keys(as)
    .filter((k) => bs[k] != null)
    .map((k) => ({ k, d: as[k] - bs[k] }))
    .filter((m) => m.d > 0)
    .sort((a, b) => b.d - a.d)
    .slice(0, n);
};

function drawCoverImage(ctx, img, x, y, w, h, radius) {
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  ctx.clip();
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale, dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

function drawEyeBar(ctx, x, y, w, h) {
  // solid bar across the eye region (faces are center-framed by the scan modal)
  const barY = y + h * 0.28, barH = h * 0.13;
  ctx.fillStyle = '#161b17';
  ctx.beginPath();
  ctx.roundRect(x + w * 0.04, barY, w * 0.92, barH, 10);
  ctx.fill();
}

function drawHeaderFooter(ctx, deltaText) {
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = GREEN;
  ctx.font = 'bold 40px Manrope, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('InnerGlow', 70, 105);
  ctx.fillStyle = MUTED;
  ctx.font = '500 26px Manrope, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('AI SKIN PROGRESS', W - 70, 103);
  ctx.textAlign = 'left';
  ctx.fillStyle = INK;
  ctx.font = 'bold 72px Manrope, sans-serif';
  ctx.fillText('My Skin Progress', 70, 210);
  if (deltaText) {
    ctx.font = 'bold 44px Manrope, sans-serif';
    const tw = ctx.measureText(deltaText).width;
    ctx.fillStyle = GREEN;
    ctx.beginPath();
    ctx.roundRect(70, 245, tw + 70, 76, 38);
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(deltaText, 105, 297);
  }
  // footer
  ctx.fillStyle = GREEN;
  ctx.fillRect(0, H - 110, W, 110);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = '600 30px Manrope, sans-serif';
  ctx.fillText('Tracked with InnerGlow — 30-Point AI Skin Analysis', 70, H - 43);
}

function drawScoreChip(ctx, cx, y, label, score, color) {
  ctx.textAlign = 'center';
  ctx.fillStyle = MUTED;
  ctx.font = '600 28px Manrope, sans-serif';
  ctx.fillText(label.toUpperCase(), cx, y);
  if (score != null) {
    ctx.fillStyle = color;
    ctx.font = 'bold 58px Manrope, sans-serif';
    ctx.fillText(`${score}`, cx, y + 62);
    ctx.fillStyle = MUTED;
    ctx.font = '500 26px Manrope, sans-serif';
    ctx.fillText('/ 100', cx, y + 96);
  }
  ctx.textAlign = 'left';
}

function drawImprovementPills(ctx, movers, startY) {
  let x = 70, y = startY;
  ctx.font = 'bold 30px Manrope, sans-serif';
  movers.forEach((m) => {
    const label = `${ATTR_META[m.k] || m.k} +${m.d}`;
    const tw = ctx.measureText(label).width;
    if (x + tw + 60 > W - 70) { x = 70; y += 76; }
    ctx.fillStyle = 'rgba(74,93,78,0.12)';
    ctx.beginPath();
    ctx.roundRect(x, y, tw + 56, 60, 30);
    ctx.fill();
    ctx.fillStyle = GREEN;
    ctx.fillText(label, x + 28, y + 41);
    x += tw + 76;
  });
  return y + 76;
}

async function drawPhotoCard(ctx, { beforeImg, afterImg, beforeDate, afterDate, beforeScore, afterScore, movers, deltaText }) {
  drawHeaderFooter(ctx, deltaText);
  const imgW = 445, imgH = 540, gap = 50, y = 370;
  const x1 = 70, x2 = x1 + imgW + gap;
  drawCoverImage(ctx, beforeImg, x1, y, imgW, imgH, 28);
  drawCoverImage(ctx, afterImg, x2, y, imgW, imgH, 28);
  drawEyeBar(ctx, x1, y, imgW, imgH);
  drawEyeBar(ctx, x2, y, imgW, imgH);
  // date ribbons on photos
  const ribbon = (x, text, bg) => {
    ctx.font = 'bold 26px Manrope, sans-serif';
    const tw = ctx.measureText(text).width;
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.roundRect(x + 20, y + 20, tw + 44, 52, 26);
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(text, x + 42, y + 55);
  };
  ribbon(x1, `BEFORE · ${beforeDate}`, 'rgba(22,27,23,0.75)');
  ribbon(x2, `AFTER · ${afterDate}`, GREEN);
  drawScoreChip(ctx, x1 + imgW / 2, y + imgH + 70, 'Before', beforeScore, MUTED);
  drawScoreChip(ctx, x2 + imgW / 2, y + imgH + 70, 'After', afterScore, GREEN);
  if (movers.length) drawImprovementPills(ctx, movers, y + imgH + 200);
}

function drawRing(ctx, cx, cy, r, score, color, label, date) {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = '#F3E6E0';
  ctx.lineWidth = 26;
  ctx.stroke();
  if (score != null) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (score / 100) * Math.PI * 2);
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.stroke();
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = INK;
  ctx.font = 'bold 92px Manrope, sans-serif';
  ctx.fillText(score != null ? `${score}` : '—', cx, cy + 30);
  ctx.fillStyle = MUTED;
  ctx.font = '600 30px Manrope, sans-serif';
  ctx.fillText(label.toUpperCase(), cx, cy + r + 70);
  ctx.font = '500 26px Manrope, sans-serif';
  ctx.fillText(date, cx, cy + r + 108);
  ctx.textAlign = 'left';
}

function drawScoresCard(ctx, { beforeDate, afterDate, beforeScore, afterScore, movers, deltaText }) {
  drawHeaderFooter(ctx, deltaText);
  const cy = 620, r = 165;
  drawRing(ctx, 300, cy, r, beforeScore, '#B9BDB4', 'Before', beforeDate);
  drawRing(ctx, 780, cy, r, afterScore, SAGE, 'After', afterDate);
  // arrow between rings
  ctx.strokeStyle = GREEN;
  ctx.lineWidth = 10;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(495, cy);
  ctx.lineTo(585, cy);
  ctx.moveTo(560, cy - 24);
  ctx.lineTo(585, cy);
  ctx.lineTo(560, cy + 24);
  ctx.stroke();
  if (movers.length) {
    ctx.fillStyle = INK;
    ctx.font = 'bold 40px Manrope, sans-serif';
    ctx.fillText('Biggest improvements', 70, 1000);
    drawImprovementPills(ctx, movers, 1035);
  }
}

const ShareProgressCard = ({ comparison, beforeAnalysis, afterAnalysis }) => {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('photos'); // photos | scores
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rendering, setRendering] = useState(false);

  const beforeScore = overallOf(beforeAnalysis);
  const afterScore = overallOf(afterAnalysis);
  const delta = beforeScore != null && afterScore != null ? afterScore - beforeScore : null;
  const deltaText = delta != null ? (delta >= 0 ? `+${delta} points` : `${delta} points`) : null;

  const render = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !comparison) return;
    setRendering(true);
    try {
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      const payload = {
        beforeDate: fmtDate(comparison.before?.date),
        afterDate: fmtDate(comparison.after?.date),
        beforeScore, afterScore,
        movers: topImprovements(beforeAnalysis, afterAnalysis),
        deltaText,
      };
      if (mode === 'photos' && comparison.before?.image && comparison.after?.image) {
        try {
          const [beforeImg, afterImg] = await Promise.all([
            loadImage(toSrc(comparison.before.image)),
            loadImage(toSrc(comparison.after.image)),
          ]);
          await drawPhotoCard(ctx, { ...payload, beforeImg, afterImg });
        } catch (imgErr) {
          drawScoresCard(ctx, payload);
          toast.info('Photos unavailable — generated a scores-only card instead.');
        }
      } else {
        drawScoresCard(ctx, payload);
      }
      setPreviewUrl(canvas.toDataURL('image/png'));
    } catch (e) {
      toast.error('Could not generate the share card.');
    } finally {
      setRendering(false);
    }
  }, [comparison, mode, beforeAnalysis, afterAnalysis, beforeScore, afterScore, deltaText]);

  useEffect(() => { render(); }, [render]);

  const shareText = `My skin progress with InnerGlow${deltaText ? ` — ${deltaText} overall` : ''}! 🌿 AI-tracked, clinically graded.`;

  const handleShare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], 'innerglow-progress.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ title: 'My Skin Progress', text: shareText, files: [file] });
          return;
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      }
      const link = document.createElement('a');
      link.download = 'innerglow-progress.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Image downloaded — ready to post!');
    }, 'image/png');
  }, [shareText]);

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'innerglow-progress.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Progress card downloaded!');
  }, []);

  const hasImages = Boolean(comparison?.before?.image && comparison?.after?.image);

  return (
    <Card className="card-elevated border-0" data-testid="share-progress-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-[#1A1514] flex items-center gap-2">
              <Share2 className="w-4 h-4 text-[#FF6B4A]" /> Share your progress
            </h3>
            <p className="text-xs text-[#8C7A76] flex items-center gap-1 mt-0.5">
              <EyeOff className="w-3 h-3" /> Anonymized — no name, eyes covered in photo mode
            </p>
          </div>
          <div className="flex rounded-full bg-[#F3E6E0] p-1" data-testid="share-mode-toggle">
            <button
              onClick={() => setMode('photos')}
              disabled={!hasImages}
              className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${mode === 'photos' ? 'bg-[#FF6B4A] text-white' : 'text-[#FF6B4A] disabled:opacity-40'}`}
              data-testid="share-mode-photos"
            >
              <ImageIcon className="w-3.5 h-3.5" /> Photos
            </button>
            <button
              onClick={() => setMode('scores')}
              className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${mode === 'scores' ? 'bg-[#FF6B4A] text-white' : 'text-[#FF6B4A]'}`}
              data-testid="share-mode-scores"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Scores only
            </button>
          </div>
        </div>

        <div className="rounded-xl overflow-hidden bg-[#F3E6E0] mb-4 relative" data-testid="share-card-preview">
          {rendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B4A]" />
            </div>
          )}
          {previewUrl ? (
            <img src={previewUrl} alt="Progress card preview" className="w-full max-h-[420px] object-contain" />
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-[#8C7A76]">Generating preview…</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button onClick={handleShare} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full h-11 font-semibold" data-testid="share-progress-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button onClick={handleDownload} variant="outline" className="rounded-full h-11 font-semibold border-[#FF6B4A]/30 text-[#FF6B4A]" data-testid="download-progress-btn">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
};

export default ShareProgressCard;
